package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.coroutines.mapToOneOrNull
import com.tedd.db.Notebook
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

class NotebookRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Notebook>> =
        db.notebookQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeById(id: String): Flow<Notebook?> =
        db.notebookQueries.selectById(id).asFlow().mapToOneOrNull(Dispatchers.Default)

    suspend fun create(
        name: String,
        icon: String,
        color: String,
        parentId: String? = null,
        orderIndex: Long = 0,
        id: String = newId("nb"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.notebookQueries.insert(id, name, icon, color, parentId, orderIndex, now, now)
        id
    }

    suspend fun update(
        id: String, name: String, icon: String, color: String, parentId: String?, orderIndex: Long,
    ) = withContext(Dispatchers.Default) {
        db.notebookQueries.update(name, icon, color, parentId, orderIndex, nowMillis(), id)
    }

    suspend fun rename(id: String, name: String) = withContext(Dispatchers.Default) {
        db.notebookQueries.rename(name, nowMillis(), id)
    }

    /**
     * Déplace un dossier sous [parentId] (NULL = racine). Profondeur illimitée,
     * mais on refuse de créer un cycle : un dossier ne peut pas devenir son propre
     * descendant. Retourne true si le déplacement a eu lieu.
     */
    suspend fun reparent(id: String, parentId: String?): Boolean = withContext(Dispatchers.Default) {
        if (parentId == id) return@withContext false
        if (parentId != null && isDescendant(candidate = parentId, ancestor = id)) return@withContext false
        db.notebookQueries.setParent(parentId, nowMillis(), id)
        true
    }

    /** True si [candidate] est [ancestor] lui-même ou l'un de ses descendants. */
    private fun isDescendant(candidate: String, ancestor: String): Boolean {
        val all = db.notebookQueries.selectAll().executeAsList()
        val childrenOf = all.groupBy { it.parent_id }
        val stack = ArrayDeque<String>()
        stack.addLast(ancestor)
        while (stack.isNotEmpty()) {
            val current = stack.removeLast()
            if (current == candidate) return true
            childrenOf[current]?.forEach { stack.addLast(it.id) }
        }
        return false
    }

    suspend fun softDelete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.notebookQueries.softDelete(now, now, id)
    }
}
